import React, { useEffect, useState, useMemo, useRef } from 'react'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box, Typography, Popover, useTheme, Chip } from '@mui/material'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'
import { useAppSelector, useAppDispatch } from '../../store/Hooks'
import ContractsManager, { CSIFieldOption, CSimilarity } from '../../managers/ContractsManager'
import { IContract } from '../../definitions/Contract'
import RouteHeader from '../controls/RouteHeader'
import Tile from '../layout/Tile'
import { changeRoute, setSelectedFields } from '../../store/slices/SessionSlice'

export default function ContractAnalysis() {
    const dispatch = useAppDispatch()
    const theme = useTheme()

    // TODO: check the real type of this prop here!!!
    const routeParams = useAppSelector<{ contracts: IContract[] }>(state => state.session.routeParams)
    const selectedFields = useAppSelector(state => state.session.selectedFields ?? [])
    const contractsFromRoute = routeParams?.contracts ?? []

    const [selectedContracts, setSelectedContracts] = useState<IContract[]>(contractsFromRoute)
    const [similarities, setSimilarities] = useState<CSimilarity[]>([])
    const [fieldsAnchorEl, setFieldsAnchorEl] = useState<HTMLElement>(null)
    const [contractsAnchorEl, setContractsAnchorEl] = useState<HTMLElement>(null)
    const [fieldsItems, setFieldsItems] = useState<TreeViewBaseItem[]>([])
    const [contractsItems, setContractsItems] = useState<TreeViewBaseItem[]>([])
    const [selectedFieldsNodes, setSelectedFieldsNodes] = useState<string[]>([])
    const [selectedContractsNodes, setSelectedContractsNodes] = useState<string[]>(selectedContracts.map(c => c.uuid))
    const [popoverWidthTerms, setPopoverWidthTerms] = useState<number | undefined>(undefined)
    const [popoverWidthContracts, setPopoverWidthContracts] = useState<number | undefined>(undefined)
    const termsRef = useRef<HTMLDivElement>(null)
    const contractsRef = useRef<HTMLDivElement>(null)

    const selectedTermsLabel = selectedFields.map(data => ContractsManager.getFieldLabel(data))
    const selectedContractsLabel = selectedContracts.map(contract => contract.name)
    const isFieldsPopoverOpen = Boolean(fieldsAnchorEl)
    const isContractsPopoverOpen = Boolean(contractsAnchorEl)

    useEffect(() => {
        if (isFieldsPopoverOpen && termsRef.current) {
            setPopoverWidthTerms(termsRef.current.clientWidth)
        }
    }, [isFieldsPopoverOpen])

    useEffect(() => {
        if (isContractsPopoverOpen && contractsRef.current) {
            setPopoverWidthContracts(contractsRef.current.clientWidth)
        }
    }, [isContractsPopoverOpen])

    useEffect(() => {
        const treeItems = buildTreeItemsFromAllContracts()
        setContractsItems(treeItems)

        if (contractsFromRoute.length > 0) {
            setSelectedContracts(contractsFromRoute)
            setSelectedContractsNodes(contractsFromRoute.map(c => c.uuid))
            setSimilarities(ContractsManager.crossSimilarities(contractsFromRoute))
        }
    }, [contractsFromRoute])

    useEffect(() => {
        if (selectedContracts.length > 0) {
            let fieldsToInclude: CSIFieldOption[] = []
            if (selectedFields.length > 0) {
                fieldsToInclude = selectedFields as CSIFieldOption[]
            } else {
                fieldsToInclude = []
            }
            const updatedSimilarities = ContractsManager.crossSimilarities(selectedContracts, fieldsToInclude)
            setSimilarities(updatedSimilarities)
        }
    }, [selectedContracts, selectedFields])

    useEffect(() => {
        const treeItems = ContractsManager.getCSIFields().map(category => ({
            id: category.label,
            label: category.label,
            children: category.fields.map(field => ({
                id: field.key,
                label: field.label
            }))
        }))
        setFieldsItems(treeItems)

        const allFieldsIds = getAllIds(treeItems)
        dispatch(setSelectedFields(allFieldsIds as CSIFieldOption[]))
    }, [])

    useEffect(() => {
        if (selectedFields.includes('all')) {
            const allIds = getAllIds(fieldsItems)
            setSelectedFieldsNodes(allIds)
        } else {
            setSelectedFieldsNodes(selectedFields)
        }
    }, [selectedFields, fieldsItems])

    const allCsiData = useMemo(() => {
        return selectedContracts.map(contract => {
            const related = similarities.find(sim => sim.from.uuid === contract.uuid)
            return {
                id: contract.uuid,
                name: contract.name,
                relationships: related
                    ? related.tos.reduce((acc, to) => {
                        acc[to.uuid] = { value: to.score }
                        return acc
                    }, {} as Record<string, { value: number }>)
                    : {}
            }
        })
    }, [selectedContracts, similarities])

    const navigateToComparisonPage = (fromContract: IContract, toContract: IContract, commonFields: string[], csiScore: number, selectedFields: string[]) => {
        dispatch(
            changeRoute({
                route: 'compare-contracts-csi',
                params: {
                    fromContract,
                    toContract,
                    commonFields,
                    allCsiData,
                    csiScore,
                    selectedFields
                }
            })
        )
    }

    const buildTreeItemsFromAllContracts = (): TreeViewBaseItem[] => {
        return contractsFromRoute.map(contract => ({
            id: contract.uuid,
            label: contract.name
        }))
    }

    const findItem = (list: TreeViewBaseItem[], id: string): TreeViewBaseItem => {
        for (const i of list) {
            if (i.id === id) return i
            if (i.children) {
                const found = findItem(i.children, id)
                if (found) return found
            }
        }

        return undefined
    }

    const getAllDescendants = (id: string, list: TreeViewBaseItem[]): string[] => {
        const root = findItem(list, id)
        const collectChildren = (item: TreeViewBaseItem): string[] => {
            let result: string[] = [item.id]
            if (item.children) {
                for (const child of item.children) {
                    result = result.concat(collectChildren(child))
                }
            }
            return result
        }
        return root ? collectChildren(root) : []
    }

    const getAllIds = (list: TreeViewBaseItem[]): string[] => {
        let result: string[] = []
        for (const i of list) {
            result.push(i.id)
            if (i.children) {
                result = result.concat(getAllIds(i.children))
            }
        }
        return result
    }

    const interpolateColor = (score: number): string => {
        // TODO: create colorhelper, move constants and interpolate
        const startColor = { r: 156, g: 191, b: 216 }
        const endColor = { r: 7, g: 50, b: 74 }
        const r = Math.round(startColor.r + (endColor.r - startColor.r) * score)
        const g = Math.round(startColor.g + (endColor.g - startColor.g) * score)
        const b = Math.round(startColor.b + (endColor.b - startColor.b) * score)
        return `rgba(${r}, ${g}, ${b}, 1)`
    }

    // TODO: NEVER USE ANYHITNG ELSE THAN ID (OR UUID) AS KEYS
    // refactor to use the id
    const handleDeleteTerm = (category: string) => {
        const fieldToRemove = selectedFields.find(field => ContractsManager.getFieldLabel(field) === category)
        if (fieldToRemove) {
            const updatedFields = selectedFields.filter(field => field !== fieldToRemove)
            dispatch(setSelectedFields(updatedFields as CSIFieldOption[]))
        }
    }

    const handleDeleteContract = (contractName: string) => {
        const contractToRemove = selectedContracts.find(contract => contract.name === contractName)
        if (contractToRemove) {
            const updatedContracts = selectedContracts.filter(contract => contract.uuid !== contractToRemove.uuid)
            setSelectedContracts(updatedContracts)
            setSelectedContractsNodes(updatedContracts.map(c => c.uuid))
        }
    }

    return (
        <Tile.Container>
            <RouteHeader primary='Contracts Comparison' />
            <Tile.Column style={{ gap: 80 }}>
                <Box sx={{ display: 'flex', gap: 4, mt: 3 }}>
                    {/* TODO: create a standard component and reuse it (or even a function inside this component) */}
                    <Box flex={1}>
                        <Typography mb={2} variant='h6' fontWeight='bold'>Select Contracts</Typography>
                        <Box
                            ref={contractsRef}
                            onClick={e => setContractsAnchorEl(e.currentTarget)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setContractsAnchorEl(e.currentTarget)
                                }
                            }}
                            tabIndex={0}
                            role='button'
                            aria-haspopup='true'
                            aria-expanded={isContractsPopoverOpen}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${theme.palette.grey[700]}`,
                                borderRadius: 2,
                                boxShadow: 1,
                                padding: 1,
                                height: 200,
                                justifyContent: 'flex-start',
                                overflow: 'auto',
                                cursor: 'pointer',
                                backgroundColor: isContractsPopoverOpen ? theme.palette.action.hover : 'inherit'
                            }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                {selectedContractsLabel.map((name, index) => (
                                    <Chip
                                        key={index}
                                        label={name}
                                        color='default'
                                        onDelete={() => handleDeleteContract(name)}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Popover
                            open={isContractsPopoverOpen}
                            anchorEl={contractsAnchorEl}
                            onClose={() => setContractsAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            // TODO: refactor to use the new prop and NOT the deprecated one
                            PaperProps={{ style: { width: popoverWidthContracts, maxHeight: '400px', overflow: 'auto' } }}>
                            <Box sx={{ p: 2 }}>
                                <RichTreeView
                                    sx={{
                                        '.Mui-selected': { backgroundColor: 'transparent !important' },
                                        '.Mui-selected:hover': { backgroundColor: 'transparent !important' }
                                    }}
                                    multiSelect
                                    checkboxSelection
                                    items={contractsItems}
                                    selectedItems={selectedContractsNodes}
                                    onSelectedItemsChange={(event, newSelectedItems) => {
                                        setSelectedContractsNodes(newSelectedItems)
                                        const newSelectedContracts = contractsFromRoute.filter(contract => newSelectedItems.includes(contract.uuid))
                                        setSelectedContracts(newSelectedContracts)
                                        setContractsAnchorEl(null)
                                    }}
                                />
                            </Box>
                        </Popover>
                    </Box>
                    <Box flex={1}>
                        <Typography mb={2} variant='h6' fontWeight='bold'>Select Terms</Typography>
                        <Box
                            ref={termsRef}
                            onClick={e => setFieldsAnchorEl(e.currentTarget)}
                            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setFieldsAnchorEl(e.currentTarget)
                                }
                            }}
                            tabIndex={0}
                            role='button'
                            aria-haspopup='true'
                            aria-expanded={isFieldsPopoverOpen}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${theme.palette.grey[700]}`,
                                borderRadius: 2,
                                boxShadow: 1,
                                padding: 1,
                                height: 200,
                                justifyContent: 'flex-start',
                                overflow: 'auto',
                                cursor: 'pointer',
                                backgroundColor: isFieldsPopoverOpen ? theme.palette.action.hover : 'inherit'
                            }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                                {selectedTermsLabel.map((category, index) => (
                                    <Chip
                                        key={index}
                                        label={category}
                                        color='default'
                                        onDelete={() => handleDeleteTerm(category)}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Popover
                            open={isFieldsPopoverOpen}
                            anchorEl={fieldsAnchorEl}
                            onClose={() => setFieldsAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{ style: { width: popoverWidthTerms, maxHeight: '400px', overflow: 'auto' } }}>
                            <Box sx={{ p: 2 }}>
                                <RichTreeView
                                    sx={{
                                        '.Mui-selected': { backgroundColor: 'transparent !important' },
                                        '.Mui-selected:hover': { backgroundColor: 'transparent !important' }
                                    }}
                                    multiSelect
                                    checkboxSelection
                                    items={fieldsItems}
                                    selectedItems={selectedFieldsNodes}
                                    onSelectedItemsChange={(event, newSelectedItems) => {
                                        const added = newSelectedItems.filter(id => !selectedFieldsNodes.includes(id))
                                        const removed = selectedFieldsNodes.filter(id => !newSelectedItems.includes(id))
                                        for (const a of added) {
                                            const descendants = getAllDescendants(a, fieldsItems)
                                            for (const d of descendants) {
                                                if (!newSelectedItems.includes(d)) {
                                                    newSelectedItems.push(d)
                                                }
                                            }
                                        }
                                        for (const r of removed) {
                                            const descendants = getAllDescendants(r, fieldsItems)
                                            for (const d of descendants) {
                                                const index = newSelectedItems.indexOf(d)
                                                if (index > -1) {
                                                    newSelectedItems.splice(index, 1)
                                                }
                                            }
                                        }
                                        const unique = Array.from(new Set(newSelectedItems))
                                        setSelectedFieldsNodes(unique)
                                        dispatch(setSelectedFields(unique as CSIFieldOption[]))
                                        setFieldsAnchorEl(null)
                                    }}
                                />
                            </Box>
                        </Popover>
                    </Box>
                    <Box flex={1}>
                        <Typography mb={2} variant='h6' fontWeight='bold'>AI Summary</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: `1px solid ${theme.palette.grey[700]}`,
                                borderRadius: 2,
                                boxShadow: 1,
                                padding: 4,
                                height: 200,
                                justifyContent: 'center',
                                overflow: 'auto'
                            }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant='body1' color='textSecondary' textAlign='center'>Coming soon</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box>
                    {selectedContracts.length > 0 && (
                        <TableContainer component={Paper} sx={{ p: 4, borderRadius: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell></TableCell>
                                        {selectedContracts.map(contract => (
                                            <TableCell key={contract.uuid}>{contract.name}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {similarities.map(sim => (
                                        <TableRow key={sim.from.uuid}>
                                            <TableCell>{sim.from.name}</TableCell>
                                            {selectedContracts.map((contract, colIndex) => {
                                                const to = sim.tos.find(to => to.uuid === contract.uuid)
                                                const backgroundColor = to ? interpolateColor(to.score) : 'rgba(7, 50, 74, 1)'
                                                return (
                                                    <TableCell
                                                        key={colIndex}
                                                        align='center'
                                                        sx={{ 'backgroundColor': backgroundColor, 'width': 160, 'height': 70, 'transition': 'background-color 0.3s ease',
                                                            'cursor': to ? 'pointer' : 'default',
                                                            '&:hover': {
                                                                backgroundColor: to ? 'rgba(7, 50, 74, 0.7)' : backgroundColor
                                                            } }}>
                                                        {to
                                                            ? (
                                                                    <Typography
                                                                        onClick={() => navigateToComparisonPage(sim.from, contract, to.commonFields, to.score, selectedFields)}
                                                                        sx={{ cursor: 'pointer', color: 'primary', textDecoration: 'underline' }}>
                                                                        {to.score.toFixed(2)}
                                                                    </Typography>
                                                                )
                                                            : '1'}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(7, 50, 74, 0.05)',
                                    padding: 3,
                                    borderRadius: 1,
                                    justifyContent: 'space-between',
                                    display: 'flex'
                                }}>
                                <Box sx={{ width: 340, display: 'flex' }}></Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignContent: 'center' }}>
                                    <Typography variant='h6' fontWeight='bold'>How to use this chart:</Typography>
                                    <Typography>
                                        This matrix shows how similar each pair of contracts is. Darker cells indicate greater alignment.
                                        Click any cell to view detailed terms.
                                    </Typography>
                                </Box>
                            </Box>
                        </TableContainer>
                    )}
                </Box>
            </Tile.Column>
        </Tile.Container>
    )
}

export interface ContractField {
    label: string
    description?: string
    key: string
    type?: string
    objectFields?: ContractField[]
}

export interface ContractCategoryOption {
    label: string
    id?: string
    fields: ContractField[]
}
